import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Container = styled.section`
  margin: 0 auto;
  padding: 4rem 1rem;
  font-family: "Manrope", sans-serif;
  background: #f8f9fc;
  overflow: hidden;
`;

const Title = styled(motion.h2)`
  font-size: 2rem;
  color: #102a8e;
  text-align: center;
  margin-bottom: 3rem;
  font-weight: bold;
`;

const StepsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  position: relative;
  max-width: 100%; // 1200px;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StepCard = styled(motion.div)`
  padding: 2rem;
  position: relative;
  overflow: hidden;
  z-index: 1;
`;

const CheckIcon = styled.div`
  width: 25px;
  height: 25px;
  background: #5575f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: white;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StepTitle = styled.h3`
  font-size: 16px;
  color: #5575f7;
  margin-bottom: 1rem;
  font-weight: 500;
  text-align: center;
`;

const DescriptionContainer = styled.div`
  position: relative;
`;

const StepDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  background: #fdfdfd;
  padding: 18px;
  border-radius: 12px;
`;

const LeafDecoration = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 34px;
  height: 34px;
  transition: all 0.3s ease;

  svg {
    width: 100%;
    height: 100%;
    transition: all 0.3s ease;
  }

  path {
    transition: fill 0.3s ease;
  }

  ${DescriptionContainer}:hover & {
    g {
      opacity: 1;
    }
    path {
      fill: #5575f7 !important;
    }
  }
`;

const ConnectingLines = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: block;
    position: absolute;
    top: 40px;
    left: 15%;
    right: 15%;
    height: 2px;
    background: repeating-linear-gradient(
      to right,
      #5575f7 0,
      #5575f7 4px,
      transparent 8px,
      transparent 16px
    );
    z-index: 0;
  }
`;

export const Approach: React.FC = () => {
  const steps = [
    {
      title: "Our community blog",
      description:
        "Our community blog thrives on engagement, and we encourage you to actively participate by sharing your experiences, asking questions, and connecting with fellow healthcare professionals. Together, we can create a supportive environment where knowledge is shared, queries are answered, and career aspirations are nurtured.",
    },
    {
      title: "The Health Professionals Hub",
      description:
        "The Health Professionals Hub Is committed to empowering you with the information and resources needed to navigate the registration processes with ease, expand your professional horizons, and excel in your chosen healthcare career path in the UK.",
    },
    {
      title: "Job search and preparation",
      description:
        "We also boast of bank of Job interview questions that will help prepare and build your confidence for interviews and securing your desired job in your desired specialty in the UK.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <Container>
      <Title initial="hidden" animate="visible" variants={itemVariants}>
        Our approach to serving your needs
      </Title>

      <StepsGrid
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <ConnectingLines />
        {steps.map((step, index) => (
          <StepCard key={index} variants={itemVariants}>
            <CheckIcon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 6L9 17L4 12"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CheckIcon>
            <StepTitle>{step.title}</StepTitle>
            <DescriptionContainer>
              <StepDescription>{step.description}</StepDescription>
              <LeafDecoration>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g opacity="0.45">
                    <path
                      d="M26.9594 0C26.9594 0 30.4931 8.645 15.3482 14.4518C7.11882 17.6062 0.798162 25.6912 8.34502 38.1805C8.97002 32.5979 11.65 20.2893 23.1737 17.4805C23.1737 17.4805 12.4481 21.4911 11.5007 39.6454C17.0119 40.4192 29.9374 40.9391 33.775 29.7223C38.8425 14.9024 26.9594 0 26.9594 0Z"
                      fill="#E6EBE3"
                    />
                    <path
                      d="M11.5057 39.5699C23.2056 34.9005 31.7187 23.9618 32.8937 10.9037C30.5018 4.45183 26.9594 0 26.9594 0C26.9594 0 30.4931 8.645 15.3482 14.4518C7.11882 17.6062 0.798162 25.6912 8.34502 38.1805C8.97002 32.5979 11.65 20.2893 23.1737 17.4805C23.1731 17.4806 12.4794 21.4812 11.5057 39.5699Z"
                      fill="#ECEFEA"
                    />
                  </g>
                </svg>
              </LeafDecoration>
            </DescriptionContainer>
          </StepCard>
        ))}
      </StepsGrid>
    </Container>
  );
};
